import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { fetchCertificates, fetchCertificateCategories } from "../../../../api/certificateAPI";
import { placeholderImage } from "../../../invalidImage";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const PDF_ICON =
  "https://upload.wikimedia.org/wikipedia/commons/8/87/PDF_file_icon.svg";

const resolveImg = (url) => {
  if (!url) return placeholderImage;
  if (url.startsWith("http")) return url;
  return `${BASE}${url}`;
};

const isPdfUrl = (url) => (url || "").split("?")[0].toLowerCase().endsWith(".pdf");

const CertificatePage = () => {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // { item }

  useEffect(() => {
    Promise.all([fetchCertificates(), fetchCertificateCategories()])
      .then(([items, cats]) => {
        setData(items);
        setCategories(cats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const filteredData = data
    .filter((item) => item.status === 1 || item.status === "1")
    .filter((item) => activeCategory === "all" || item.type === activeCategory)
    .sort((a, b) => {
      // featured (is_featured) first
      if ((b.is_featured ? 1 : 0) !== (a.is_featured ? 1 : 0))
        return (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0);
      const dateA = a.created_at ? new Date(a.created_at) : 0;
      const dateB = b.created_at ? new Date(b.created_at) : 0;
      return dateB - dateA || b.id - a.id;
    });

  const tabs = [{ key: "all", label: "All" }, ...categories.map((c) => ({ key: c, label: c }))];

  return (
    <div>
      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveCategory(tab.key)}
            className={`px-4 py-1.5 text-xs font-bold rounded-xl transition-all duration-200 ${
              activeCategory === tab.key
                ? "bg-blue-600 text-white shadow-lg shadow-blue-900/30"
                : "bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700 hover:border-zinc-600"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <LoadingGrid />
      ) : filteredData.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filteredData.map((item, i) => (
            <CertCard key={i} item={item} onClick={() => setModal({ item })} />
          ))}
        </div>
      )}

      {/* Modal — rendered via portal to escape overflow:hidden parent */}
      {modal && ReactDOM.createPortal(
        <CertModal item={modal.item} onClose={() => setModal(null)} />,
        document.body
      )}
    </div>
  );
};

// ── Premium Modal ─────────────────────────────────────────────────────────────
const CertModal = ({ item, onClose }) => {
  const isPdf = isPdfUrl(item.img);
  const fileUrl = resolveImg(item.img);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/85 backdrop-blur-md"
        onClick={onClose}
      />

      {/* Modal panel */}
      <div className="relative z-[101] w-full max-w-3xl flex flex-col bg-[#111116] border border-zinc-700/80 rounded-3xl shadow-2xl shadow-black/60 overflow-hidden animate-modal-in">

        {/* Top accent */}
        <div className="h-0.5 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-cyan-400 flex-shrink-0" />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-5 pb-4 flex-shrink-0">
          <div className="flex-1 min-w-0 pr-4">
            <div className="flex items-center gap-2 mb-1">
              {item.type && (
                <span className="px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-blue-600/20 text-blue-400 border border-blue-600/30">
                  {item.type}
                </span>
              )}
              {(item.is_featured === 1 || item.is_featured === true) && (
                <span className="flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-widest bg-yellow-500/10 text-yellow-400 border border-yellow-500/30">
                  <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                  Featured
                </span>
              )}
            </div>
            <h2 className="text-white font-black text-lg leading-snug">{item.name}</h2>
            {item.organizer && (
              <p className="text-blue-400 text-sm font-semibold mt-0.5">{item.organizer}</p>
            )}
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 w-9 h-9 rounded-xl bg-zinc-800 hover:bg-red-500/80 border border-zinc-700 hover:border-red-500 text-zinc-400 hover:text-white flex items-center justify-center transition-all duration-200"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 border-t border-zinc-800">
          {isPdf ? (
            <iframe
              src={fileUrl}
              title={item.name}
              className="w-full h-[60vh] border-none bg-zinc-950"
            />
          ) : (
            <div className="relative bg-zinc-950 flex items-center justify-center" style={{ minHeight: "300px", maxHeight: "65vh" }}>
              <img
                src={fileUrl}
                alt={item.name}
                className="max-w-full max-h-[65vh] object-contain"
                onError={(e) => { e.target.src = placeholderImage; }}
              />
              {/* Subtle vignette */}
              <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.3)_100%)]" />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-zinc-800 flex-shrink-0 gap-3 flex-wrap">
          <div className="text-[10px] text-zinc-600 font-mono uppercase tracking-widest">
            {isPdf ? "PDF Document" : "Certificate Image"}
          </div>
          <div className="flex items-center gap-2">
            {item.link && (
              <a
                href={/^https?:\/\//i.test(item.link) ? item.link : `https://${item.link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 hover:text-white text-xs font-bold transition-all duration-200"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
                View Source
              </a>
            )}
            <a
              href={fileUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold transition-all duration-200"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download
            </a>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes modalIn {
          from { opacity: 0; transform: scale(0.96) translateY(12px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
        .animate-modal-in {
          animation: modalIn 0.2s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

// ── Card ──────────────────────────────────────────────────────────────────────
const CertCard = ({ item, onClick }) => {
  const isPdf = isPdfUrl(item.img);
  const thumbSrc = isPdf ? PDF_ICON : resolveImg(item.img);
  const isFeatured = item.is_featured === 1 || item.is_featured === true;

  return (
    <div
      onClick={onClick}
      className="group relative h-56 overflow-hidden rounded-2xl bg-zinc-900 border border-zinc-800 hover:border-blue-600/50 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-blue-900/20 cursor-pointer"
    >
      {/* Background */}
      <div
        className="absolute inset-0 transition-transform duration-500 group-hover:scale-105 bg-cover bg-center"
        style={{
          backgroundImage: `url(${thumbSrc})`,
          backgroundSize: isPdf ? "contain" : "cover",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10 group-hover:from-black/95 transition-all duration-300" />

      {/* Badges top-left */}
      <div className="absolute top-3 left-3 flex items-center gap-1.5">
        {item.type && (
          <span className="px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-widest bg-blue-600/80 text-white backdrop-blur-sm">
            {item.type}
          </span>
        )}
        {isFeatured && (
          <span className="w-6 h-6 rounded-lg bg-yellow-500/80 backdrop-blur-sm flex items-center justify-center" title="Featured">
            <svg className="w-3.5 h-3.5 text-white" fill="currentColor" viewBox="0 0 20 20">
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          </span>
        )}
      </div>

      {/* View icon */}
      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <div className="w-8 h-8 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center">
          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
        </div>
      </div>

      {/* Bottom content */}
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <h3 className="text-white font-bold text-sm leading-snug group-hover:text-blue-300 transition-colors">{item.name}</h3>
        {item.organizer && (
          <div className="mt-1 flex items-center gap-2">
            <div className="h-0.5 w-6 rounded-full bg-blue-500 group-hover:w-10 transition-all duration-300" />
            <span className="text-[10px] text-zinc-400 italic">{item.organizer}</span>
          </div>
        )}
      </div>
    </div>
  );
};

const LoadingGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
    {[...Array(6)].map((_, i) => (
      <div key={i} className="h-56 rounded-2xl bg-zinc-800/50 border border-zinc-700/50 animate-pulse" />
    ))}
  </div>
);

const EmptyState = () => (
  <div className="py-20 flex flex-col items-center justify-center text-zinc-600">
    <svg className="w-10 h-10 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
    <p className="text-sm font-semibold">No certificates found</p>
  </div>
);

export default CertificatePage;
